import React, { useCallback, useEffect, useState } from 'react';
import { copyMyKeyToAgency, getMyCredential, saveMyCredential, type InstallerCredentialStatus, type KeyTarget } from './apiIntegrationService';
import './apiintegrations.css';

// Nobody's calls use someone else's API key: everyone who installs an
// integration adds their own, or their Agency Admin adds one for the agency.
// Keys are write-only here - only whether one is set and its last 4 come back.

export function keyOwnerLabel(status: InstallerCredentialStatus): string {
  return status.target === 'agency' || status.source === 'agency' ? "your agency's API key" : 'your API key';
}

/** Masked key field shared by the Connect step, the install modal and the Installed panel. */
export const IntegrationKeyInput: React.FC<{
  value: string; onChange: (value: string) => void; placeholder?: string; disabled?: boolean; id?: string;
}> = ({ value, onChange, placeholder, disabled, id }) => (
  <input id={id} className="mt-input" type="password" autoComplete="off" spellCheck={false}
    value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder ?? 'Paste the API key'} disabled={disabled} />
);

/** Add or replace one key - the person's own or their agency's. */
const KeyEditor: React.FC<{
  id: string; label: string; busy: boolean; onSave: (secret: string) => Promise<void>;
}> = ({ id, label, busy, onSave }) => {
  const [secret, setSecret] = useState('');
  return (
    <div className="mt-field mt-field--full" style={{ marginTop: 10 }}>
      <label className="mt-label" htmlFor={id}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <IntegrationKeyInput id={id} value={secret} onChange={setSecret} disabled={busy} />
        <button type="button" className="mt-btn mt-btn--primary mt-btn--sm" disabled={busy || !secret.trim()}
          onClick={() => onSave(secret.trim()).then(() => setSecret(''), () => undefined)}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

/** Installed detail: the key this person's calls use, and adding or replacing it. An
 *  Agency Admin also manages their agency's key here, even when their own calls use
 *  their personal key - otherwise an admin who installed it personally (its creator,
 *  for one) could never set the key the rest of the agency needs. */
export const IntegrationKeyPanel: React.FC<{ integrationId: number; onSaved?: () => void }> = ({ integrationId, onSaved }) => {
  const [status, setStatus] = useState<InstallerCredentialStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setStatus(await getMyCredential(integrationId));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Couldn't load the key status.");
    }
  }, [integrationId]);

  useEffect(() => { load(); }, [load]);

  if (loadError) {
    return (
      <div>
        <p className="ai-error">{loadError}</p>
        <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!status) return <p className="ai-hint">Loading…</p>;
  if (!status.required) return null;

  const run = async (action: () => Promise<InstallerCredentialStatus>) => {
    setSaving(true);
    setMessage(null);
    try {
      setStatus(await action());
      setMessage({ ok: true, text: 'Key saved.' });
      onSaved?.();
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : "Couldn't save the key." });
      throw err;
    } finally {
      setSaving(false);
    }
  };
  const saveKey = (target: KeyTarget) => (secret: string) => run(() => saveMyCredential(integrationId, secret, target)).then(() => undefined);

  // A null target means their Agency Admin manages the key their calls use.
  const forAgency = status.target !== 'me';
  // Shown only when the panel's main key is the admin's own; when their calls already
  // use the agency's key, the main section is the agency's key.
  const agencyKey = status.target === 'me' ? status.agency_key : undefined;
  const ownKey = status.own_key;
  return (
    <div className="ai-key-panel">
      <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
        {forAgency
          ? "Calls from everyone in your agency use your agency's API key, entered by an Agency Admin. It's never used by anyone outside your agency."
          : "Calls from you use your own API key. It's never used by anyone else."}
      </p>
      <dl className="ai-detail-grid">
        <dt>Key</dt>
        <dd>
          {status.set
            ? <>{status.source === 'agency' ? "Your agency's key" : 'Your key'}, ending in <span className="ai-mono">{status.last4}</span></>
            : <span className="ai-warn-text">Not added yet - calls won't work until it is</span>}
        </dd>
      </dl>
      {status.can_edit ? (
        <KeyEditor id={`ai-key-${integrationId}`} busy={saving} onSave={saveKey(status.target ?? 'me')}
          label={status.target === 'agency'
            ? (status.set ? "Replace your agency's key" : "Add your agency's key")
            : (status.set && status.source === 'you' ? 'Replace your key' : 'Add your key')} />
      ) : (
        <p className="ai-hint" style={{ marginTop: 8 }}>Your Agency Admin manages this key for your agency.</p>
      )}
      {agencyKey && (
        <div style={{ marginTop: 16 }}>
          <p className="mt-step-subtitle" style={{ margin: '0 0 8px' }}>
            Everyone who has this through your agency's install uses your agency's key. As an Agency Admin, you manage it here.
          </p>
          <dl className="ai-detail-grid">
            <dt>Agency key</dt>
            <dd>
              {agencyKey.set
                ? <>Ending in <span className="ai-mono">{agencyKey.last4}</span></>
                : <span className="ai-warn-text">Not added yet - your agency's calls won't work until it is</span>}
            </dd>
          </dl>
          <KeyEditor id={`ai-agency-key-${integrationId}`} busy={saving} onSave={saveKey('agency')}
            label={agencyKey.set ? "Replace your agency's key" : "Add your agency's key"} />
          {ownKey?.set && ownKey.last4 !== agencyKey.last4 && (
            <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" style={{ marginTop: 8 }} disabled={saving}
              onClick={() => run(() => copyMyKeyToAgency(integrationId)).catch(() => undefined)}>
              Use my key (ending {ownKey.last4}) for the agency
            </button>
          )}
        </div>
      )}
      {message && <p className={message.ok ? 'ai-success-text' : 'ai-error'} role={message.ok ? 'status' : 'alert'}>{message.text}</p>}
    </div>
  );
};
